import React, {ReactNode, useEffect, useState} from 'react';

import '@reach/dialog/styles.css';
import {useTranslation} from 'react-i18next';

import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';

import {Icon} from '../Icon';

import {
    DialogCloseButton,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogTitle,
} from './styled';

interface Props {
    show: boolean;
    onClose: Function;
    children: ReactNode;
    title?: string | ReactNode;
    hideCloseButton?: boolean;
    footer?: ReactNode;
    className?: string;
    modalWidth?: 0 | 1 | 2 | 3;
    closable?: boolean;
    titleNoPadding?: boolean;
}

export function Modal({
    show,
    onClose,
    children,
    title,
    hideCloseButton,
    footer,
    modalWidth,
    className,
    closable = true,
    titleNoPadding = false,
}: Props) {
    const {t} = useTranslation();
    const [visible, setVisible] = useState(show ?? true);

    useEffect(() => {
        if (show === undefined) return;
        if (show) setVisible(true);
        else {
            const timeout = setTimeout(() => setVisible(false), 200);
            return () => clearTimeout(timeout);
        }
    }, [show]);

    if (!visible) return null;

    return (
        <DialogOverlay
            mode={modalWidth}
            className={className}
            open={show}
            onDismiss={() => closable && onClose()}
        >
            <DialogContent
                aria-label={typeof title === 'string' ? title : t('components.modal.ModalWindow')}
                mode={modalWidth}
                open={show}
            >
                {!hideCloseButton && (
                    <DialogCloseButton
                        icon={
                            <Icon source={CrossIcon} fillColor="#C4C4C4" width={12} height={12} />
                        }
                        onClick={() => closable && onClose()}
                    />
                )}
                <DialogHeader $hasTitle={!!title} $titleNoPadding={titleNoPadding}>
                    {typeof title === 'string' && <DialogTitle size={4}>{title}</DialogTitle>}
                    {typeof title !== 'string' && title}
                </DialogHeader>
                {children}
                {footer && <DialogFooter>{footer}</DialogFooter>}
            </DialogContent>
        </DialogOverlay>
    );
}
